// Central translation dictionary for the site.
// Usage: const { language } = useLanguage(); const t = content[language];

export const content = {
  es: {
    navbar: {
      work: 'Cómo trabajamos',
      cta: 'Agendar diagnóstico',
      home: 'Home'
    },
    hero: {
      badge: 'Consultoría y Automatización para negocios',
      titleLine1: 'Tu negocio no necesita más tecnología.',
      titleLine2: 'Necesita dejar de perder tiempo y clientes.',
      subtitle:
        'Analizamos cómo funciona tu negocio, encontramos qué procesos te hacen perder tiempo, dinero o clientes, y te proponemos una solución solo si realmente vale la pena.',
      ctaPrimary: 'Agendar diagnóstico gratuito',
      ctaSecondary: 'Conocer cómo trabajamos',
      checklist: ['Diagnóstico sin costo', 'Sin compromiso', 'Solo proponemos soluciones que aporten valor'],
      idealFor:
        'Ideal para: restaurantes, clínicas, bufetes, hoteles, gimnasios y pequeñas empresas que quieren organizar mejor sus procesos.',
      scroll: 'Scroll'
    },
    about: {
      titleLine1: '¿Te suena',
      titleLine2: 'familiar?',
      points: [
        'Los clientes escriben y algunos mensajes se quedan sin responder.',
        'La información está entre WhatsApp, Excel, papel o varias personas.',
        'Hay tareas que se repiten todos los días y consumen demasiado tiempo.',
        'Todo depende de que el dueño esté presente.',
        'Sabes que algo podría hacerse mejor, pero no sabes por dónde empezar.'
      ],
      closingTitle: 'No empezamos vendiendo',
      closingTitleHighlight: 'una página web o un sistema.',
      closingText: 'Primero entendemos cómo funciona tu negocio. Después decidimos juntos qué realmente necesita.'
    },
    trust: {
      badge: 'Cómo trabajamos',
      titleLine1: 'Un proceso',
      titleLine2: 'simple',
      subtitle: 'Antes de proponerte algo, primero entendemos tu negocio a fondo.',
      steps: [
        { title: 'Conversamos', desc: 'Conocemos cómo funciona tu negocio y entendemos dónde están las dificultades.' },
        { title: 'Encontramos el problema', desc: 'Identificamos qué procesos están haciendo perder tiempo, clientes o dinero.' },
        { title: 'Diseñamos la solución', desc: 'Si una página web, una automatización o un sistema pueden ayudarte, te presentamos una propuesta clara y sin compromiso.' },
        { title: 'Implementamos', desc: 'Construimos la solución y medimos que realmente genere resultados.' }
      ]
    },
    portfolio: {
      badge: 'Portafolio',
      titleLine1: 'Algunos negocios',
      titleLine2: 'con los que hemos trabajado',
      subtitle: 'Cada proyecto empezó con una conversación para entender el negocio, no con una plantilla.',
      viewProject: 'Ver proyecto',
      descriptions: {
        'Grano Santo': 'Landing page para café de especialidad',
        'FundArte Cojutepeque': 'Sitio web para fundación artística',
        'Clínica Dental Premium': 'Landing page para clínica dental de alto nivel',
        'Bufete': 'Sitio web para despacho jurídico',
        'Hotel Boutique': 'Landing page para hotel boutique',
        'Gimnasio': 'Landing page para gimnasio y fitness'
      }
    },
    faq: {
      heading: 'Preguntas Frecuentes',
      items: [
        { question: '¿El diagnóstico realmente es gratuito?', answer: 'Sí. Conversamos sobre tu negocio, identificamos oportunidades de mejora y te compartimos nuestras recomendaciones sin ningún costo.' },
        { question: '¿Tengo que contratar algo después?', answer: 'No. Si creemos que podemos ayudarte, te presentaremos una propuesta. Si no vemos una oportunidad donde realmente agreguemos valor, te lo diremos con total honestidad.' },
        { question: '¿Cuánto dura el diagnóstico?', answer: 'Normalmente una sola conversación de 30 a 45 minutos es suficiente para entender tu negocio y detectar dónde puedes mejorar.' },
        { question: '¿Para qué tipo de negocios trabajan?', answer: 'Restaurantes, clínicas, bufetes, hoteles, gimnasios y pequeñas empresas que quieren organizar mejor sus procesos.' },
        { question: '¿Qué pasa si ya tengo una página web o un sistema?', answer: 'Perfecto. Analizamos lo que ya tienes y te decimos con honestidad si vale la pena ajustarlo, complementarlo, o si ya está funcionando bien.' }
      ]
    },
    cta: {
      titleLine1: 'Antes de invertir en tecnología,',
      titleLine2: 'entendamos tu negocio.',
      subtitle: 'Agenda un diagnóstico gratuito y descubre dónde puedes ahorrar tiempo, mejorar tus procesos y ofrecer una mejor experiencia a tus clientes.',
      pillPart1: 'Sin costo.',
      pillPart2: 'Sin compromiso.',
      button: '📲 Agendar diagnóstico por WhatsApp',
      whatsappMessage: 'Hola DigitalFlow, me interesa agendar un diagnóstico gratuito para mi negocio'
    },
    footer: {
      tagline: 'Ayudamos a negocios a encontrar qué les hace perder tiempo, dinero o clientes — y a resolverlo con la tecnología correcta.',
      home: 'Inicio',
      work: 'Cómo trabajamos',
      location: 'San Salvador, El Salvador',
      rights: 'Todos los derechos reservados.'
    },
    empezar: {
      metaTitle: 'Empieza tu proyecto | DigitalFlow',
      metaDescription: 'Cuéntanos sobre tu negocio en 4 pasos rápidos y te preparamos una propuesta a tu medida.',
      badge: 'Empieza tu proyecto',
      headingLine1: 'Cuéntanos sobre',
      headingHighlight: 'tu negocio',
      stepLabel: (step, total) => `Paso ${step} de ${total}`,
      completedLabel: '¡Completado!',
      steps: [
        {
          badge: 'Tu negocio',
          title: '¿Qué tipo de negocio tienes?',
          subtitle: 'Esto nos ayuda a entender mejor lo que necesitas.',
          options: [
            { label: 'Restaurante / Café' },
            { label: 'Clínica / Salud' },
            { label: 'Servicios profesionales' },
            { label: 'Tienda / Productos' },
            { label: 'Hotel / Turismo' },
            { label: 'Otro' }
          ]
        },
        {
          badge: 'Tu proyecto',
          title: '¿Qué necesitas?',
          subtitle: 'Si no estás seguro, no te preocupes — te asesoramos.',
          options: [
            { label: 'Landing page', detail: 'Una página enfocada en convertir visitantes en clientes' },
            { label: 'Sitio web completo', detail: 'Varias secciones para presentar todo tu negocio' },
            { label: 'Catálogo digital', detail: 'Muestra productos y recibe pedidos por WhatsApp' },
            { label: 'Aún no estoy seguro', detail: 'Quiero que me orienten según mi negocio' }
          ]
        },
        {
          badge: 'Inversión',
          title: '¿Cuál es tu presupuesto aproximado?',
          subtitle: 'Esto nos ayuda a recomendarte la mejor opción para tu negocio.',
          options: [
            { label: '$200 – $400', detail: 'Pago único' },
            { label: '$400 – $800', detail: 'Pago único' },
            { label: 'Más de $800', detail: 'Proyecto más completo' },
            { label: 'Prefiero mensualidades', detail: 'Pagos mensuales en lugar de un solo pago' },
            { label: 'Aún no lo sé', detail: 'Quiero conocer opciones primero' }
          ]
        },
        {
          badge: 'Tiempos',
          title: '¿Cuándo te gustaría empezar?',
          subtitle: 'Así sabemos qué tan pronto reservar tu espacio.',
          options: [
            { label: 'Lo antes posible' },
            { label: 'Este mes' },
            { label: 'En 1 a 3 meses' },
            { label: 'Solo estoy explorando' }
          ]
        }
      ],
      contact: {
        title: '¡Último paso!',
        subtitle: 'Déjanos tus datos y te contactamos con una propuesta personalizada.',
        nameLabel: 'Tu nombre *',
        namePlaceholder: 'Ej. María López',
        phoneLabel: 'Tu WhatsApp *',
        phonePlaceholder: 'Ej. 7900 9458',
        businessNameLabel: 'Nombre de tu negocio',
        businessNameOptional: '(opcional)',
        businessNamePlaceholder: 'Ej. Café El Volcán',
        submitting: 'Enviando...',
        submit: 'Recibir mi propuesta',
        disclaimer: 'Sin compromiso. Solo usamos tus datos para contactarte sobre tu proyecto.',
        errorName: 'Ingresa tu nombre',
        errorPhone: 'Ingresa tu número de WhatsApp',
        submitError: 'No pudimos enviar tu información. Intenta de nuevo o escríbenos directo por WhatsApp.'
      },
      success: {
        thanks: (name) => `¡Gracias, ${name}!`,
        message: 'Recibimos tu información. Te contactaremos por WhatsApp muy pronto para preparar una propuesta a tu medida.'
      },
      back: 'Volver al paso anterior'
    }
  },

  en: {
    navbar: {
      work: 'How we work',
      cta: 'Book diagnostic',
      home: 'Home'
    },
    hero: {
      badge: 'Consulting & Automation for businesses',
      titleLine1: "Your business doesn't need more technology.",
      titleLine2: 'It needs to stop losing time and customers.',
      subtitle:
        'We analyze how your business runs, find which processes are costing you time, money, or customers, and propose a solution only if it truly makes sense.',
      ctaPrimary: 'Book a free diagnostic',
      ctaSecondary: 'See how we work',
      checklist: ['Free diagnostic', 'No commitment', 'We only propose solutions that add real value'],
      idealFor:
        'Ideal for: restaurants, clinics, law firms, hotels, gyms, and small businesses that want to run their processes better.',
      scroll: 'Scroll'
    },
    about: {
      titleLine1: 'Does this',
      titleLine2: 'sound familiar?',
      points: [
        'Customers message you and some texts never get a reply.',
        "Information is scattered across WhatsApp, Excel, paper, or several people's heads.",
        'Some tasks repeat every single day and eat up too much time.',
        'Everything depends on the owner being there in person.',
        "You know something could work better, but you're not sure where to start."
      ],
      closingTitle: "We don't start by selling",
      closingTitleHighlight: 'a website or a system.',
      closingText: 'We first understand how your business works. Then we decide together what it actually needs.'
    },
    trust: {
      badge: 'How we work',
      titleLine1: 'A simple',
      titleLine2: 'process',
      subtitle: "Before proposing anything, we get to know your business inside out.",
      steps: [
        { title: 'We talk', desc: 'We learn how your business runs and understand where the friction is.' },
        { title: 'We find the problem', desc: "We identify which processes are costing you time, customers, or money." },
        { title: 'We design the solution', desc: 'If a website, an automation, or a system can help, we present a clear, no-commitment proposal.' },
        { title: 'We implement', desc: 'We build the solution and measure that it actually delivers results.' }
      ]
    },
    portfolio: {
      badge: 'Portfolio',
      titleLine1: 'Some businesses',
      titleLine2: "we've worked with",
      subtitle: "Every project started with a conversation to understand the business, not a template.",
      viewProject: 'View project',
      descriptions: {
        'Grano Santo': 'Landing page for a specialty coffee shop',
        'FundArte Cojutepeque': 'Website for an arts foundation',
        'Clínica Dental Premium': 'Landing page for a premium dental clinic',
        'Bufete': 'Website for a law firm',
        'Hotel Boutique': 'Landing page for a boutique hotel',
        'Gimnasio': 'Landing page for a gym and fitness studio'
      }
    },
    faq: {
      heading: 'Frequently Asked Questions',
      items: [
        { question: 'Is the diagnostic really free?', answer: 'Yes. We talk about your business, identify opportunities to improve, and share our recommendations at no cost.' },
        { question: 'Do I have to hire something afterward?', answer: "No. If we believe we can help you, we'll present a proposal. If we don't see an opportunity where we'd truly add value, we'll tell you honestly." },
        { question: 'How long does the diagnostic take?', answer: 'Usually a single 30 to 45 minute conversation is enough to understand your business and spot where you can improve.' },
        { question: 'What kind of businesses do you work with?', answer: 'Restaurants, clinics, law firms, hotels, gyms, and small businesses that want to run their processes better.' },
        { question: 'What if I already have a website or a system?', answer: "Perfect. We'll look at what you already have and tell you honestly whether it's worth adjusting, complementing it, or if it's already working well." }
      ]
    },
    cta: {
      titleLine1: 'Before investing in technology,',
      titleLine2: "let's understand your business.",
      subtitle: 'Book a free diagnostic and discover where you can save time, improve your processes, and give your customers a better experience.',
      pillPart1: 'No cost.',
      pillPart2: 'No commitment.',
      button: '📲 Book a diagnostic on WhatsApp',
      whatsappMessage: "Hi DigitalFlow, I'd like to book a free diagnostic for my business"
    },
    footer: {
      tagline: "We help businesses find what's costing them time, money, or customers — and fix it with the right technology.",
      home: 'Home',
      work: 'How we work',
      location: 'San Salvador, El Salvador',
      rights: 'All rights reserved.'
    },
    empezar: {
      metaTitle: 'Start your project | DigitalFlow',
      metaDescription: "Tell us about your business in 4 quick steps and we'll prepare a proposal tailored to you.",
      badge: 'Start your project',
      headingLine1: 'Tell us about',
      headingHighlight: 'your business',
      stepLabel: (step, total) => `Step ${step} of ${total}`,
      completedLabel: 'Completed!',
      steps: [
        {
          badge: 'Your business',
          title: 'What type of business do you have?',
          subtitle: 'This helps us understand what you need.',
          options: [
            { label: 'Restaurant / Café' },
            { label: 'Clinic / Health' },
            { label: 'Professional services' },
            { label: 'Store / Products' },
            { label: 'Hotel / Tourism' },
            { label: 'Other' }
          ]
        },
        {
          badge: 'Your project',
          title: 'What do you need?',
          subtitle: "Not sure? Don't worry — we'll guide you.",
          options: [
            { label: 'Landing page', detail: 'A page focused on turning visitors into customers' },
            { label: 'Full website', detail: 'Several sections to present your whole business' },
            { label: 'Digital catalog', detail: 'Show products and take orders via WhatsApp' },
            { label: "Not sure yet", detail: "I'd like guidance based on my business" }
          ]
        },
        {
          badge: 'Investment',
          title: "What's your approximate budget?",
          subtitle: 'This helps us recommend the best option for your business.',
          options: [
            { label: '$200 – $400', detail: 'One-time payment' },
            { label: '$400 – $800', detail: 'One-time payment' },
            { label: 'More than $800', detail: 'A more complete project' },
            { label: 'I prefer monthly payments', detail: 'Monthly payments instead of one lump sum' },
            { label: "I don't know yet", detail: 'I want to see the options first' }
          ]
        },
        {
          badge: 'Timing',
          title: 'When would you like to start?',
          subtitle: 'This helps us know how soon to reserve your spot.',
          options: [
            { label: 'As soon as possible' },
            { label: 'This month' },
            { label: 'In 1 to 3 months' },
            { label: 'Just exploring' }
          ]
        }
      ],
      contact: {
        title: 'Last step!',
        subtitle: "Leave us your details and we'll reach out with a personalized proposal.",
        nameLabel: 'Your name *',
        namePlaceholder: 'e.g. Maria Lopez',
        phoneLabel: 'Your WhatsApp *',
        phonePlaceholder: 'e.g. 7900 9458',
        businessNameLabel: 'Business name',
        businessNameOptional: '(optional)',
        businessNamePlaceholder: 'e.g. El Volcán Café',
        submitting: 'Sending...',
        submit: 'Get my proposal',
        disclaimer: "No commitment. We only use your details to contact you about your project.",
        errorName: 'Enter your name',
        errorPhone: 'Enter your WhatsApp number',
        submitError: "We couldn't send your information. Try again or message us directly on WhatsApp."
      },
      success: {
        thanks: (name) => `Thank you, ${name}!`,
        message: "We received your information. We'll reach out on WhatsApp shortly to prepare a proposal tailored to you."
      },
      back: 'Back to previous step'
    }
  }
};

export default content;
